// Pure selector for the "No to all remaining" bulk action, extracted from
// CategoryQuestionnaireComponent so the qualifying-group rule is unit-testable in isolation.
//
// Provider pain (saves ~25 clicks): the save-time auto-deny (buildBatchSavePayload) only
// fires for batch groups the provider TOUCHED. A group never touched is left alone, so each
// untouched group needs its own manual "No" click. This selector picks every group that has
// no positive answer yet so the handler can apply the EXISTING per-group deny path to all of
// them in one click (the consequence — the red "No" chip — becomes visible before Save).
//
// This module owns ONLY the selection rule. The actual deny semantics (value resolution,
// queObj/data writes, the F122 pertinent-positive guard) stay in handleBatchDenyAll — this
// never re-implements them.

// Mirror BatchChipGroup's selected-state rule: a recommended item counts as "positive"
// (a non-No entry) when its first_response is Yes / 4 / 5.
const POSITIVE_RESPONSES = new Set<string | number>(['Yes', '5', '4', 5, 4])

// Keep in sync with EXPOSURE_DEPENDENT_CATEGORIES (BatchChipGroup.tsx) /
// EXPOSURE_DEPENDENT_CATEGORIES_GUARD (category-quesionnaries.component.tsx). DM-7562:
// exposure-dependent groups must never cascade-deny unticked siblings.
const EXPOSURE_DEPENDENT_CATEGORIES = new Set(['ALLEVIATING', 'AGGRAVATING'])

export interface BatchGroupItems {
  bg: string
  items: any[]
}

const recommendedItems = (items: any[]) => items.filter((i: any) => i.is_recommended !== false)

const hasPositive = (items: any[]) =>
  items.some((i: any) => POSITIVE_RESPONSES.has(i.first_response))

const isExposureDependent = (items: any[]) =>
  EXPOSURE_DEPENDENT_CATEGORIES.has(items[0]?.ui_category)

/**
 * A batch group qualifies for "No to all remaining" iff:
 *  - it is not exposure-dependent (ALLEVIATING/AGGRAVATING), AND
 *  - it has at least one recommended (denyable) item, AND
 *  - none of its recommended items already carries a positive answer.
 *
 * Groups that already have a "non-No" entry are left untouched; already-all-denied groups
 * still qualify but re-denying them is idempotent.
 */
export function selectGroupsToDeny(batchGroupItems: BatchGroupItems[]): BatchGroupItems[] {
  return batchGroupItems.filter(({ items }) => {
    if (isExposureDependent(items)) return false
    const recommended = recommendedItems(items)
    if (recommended.length === 0) return false
    return !hasPositive(recommended)
  })
}
